import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Bot,
  Mail,
  MessageCircleMore,
  Send,
  Smartphone,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import api from "../lib/api";
import { CapabilityStatus } from "../components/CapabilityStatus";
import { useToasts } from "../components/Toasts";
import "./CommunicationsPage.css";

export default function CommunicationsPage() {
  const toast = useToasts((s) => s.push);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/focus_items?status=open&limit=300")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => toast("Could not load communications attention queue.", "err"));
  }, [toast]);

  const queue = useMemo(() => items.filter((item) =>
    item.kind === "waiting_on" || item.kind === "needs_you" || item.source === "email"
  ), [items]);

  const draftWithAI = async () => {
    if (!selected || busy) return;
    setBusy(true); setDraft("");
    try {
      const prompt = `Help me draft the communication needed for this FocusLinc item: "${selected.title}". ${selected.notes ? `Context: ${selected.notes}` : ""} Do not claim it was sent. Give me a concise draft and identify any missing recipient/context I should confirm.`;
      const { data } = await api.post("/focus/partner", { message: prompt, history: [] });
      setDraft(data.reply || "");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast(typeof detail === "string" ? detail : "AI drafting unavailable.", "err");
    } finally { setBusy(false); }
  };

  return (
    <div className="comms-page">
      <header className="comms-hero">
        <div><small>COMMANDLINC · COMMUNICATIONS</small><h1>Communication starts with what needs attention.</h1><p>Bring outreach, follow-up and messaging into the same operating flow. Draft with AI now; connect delivery channels when their providers are configured.</p></div>
      </header>

      <section className="comms-channels">
        <article className="tone-blue"><span><Mail size={18} /></span><div><b>Email</b><small>LincWerks workflow exists; delivery provider is not connected</small></div><CapabilityStatus status="not_connected" compact /></article>
        <article className="tone-purple"><span><Smartphone size={18} /></span><div><b>SMS / mobile</b><small>LincWerks workflow exists; provider decision and connection remain open</small></div><CapabilityStatus status="not_connected" compact /></article>
        <article className="tone-cyan"><span><Bot size={18} /></span><div><b>AI drafting</b><small>Uses the live FocusLinc partner</small></div><CapabilityStatus status="live" compact /></article>
        <article className="tone-teal"><span><Target size={18} /></span><div><b>Attention queue</b><small>Waiting-on and needs-you workflow is usable now</small></div><CapabilityStatus status="workflow_ready" compact /></article>
      </section>

      <section className="comms-map">
        <article><span>1</span><MessageCircleMore size={16} /><b>Signal</b><small>FocusLinc identifies follow-up</small><ArrowRight size={13} /></article>
        <article><span>2</span><Sparkles size={16} /><b>Draft</b><small>AI helps write the communication</small><ArrowRight size={13} /></article>
        <article><span>3</span><Target size={16} /><b>Review</b><small>You confirm recipient and intent</small><ArrowRight size={13} /></article>
        <article><span>4</span><Send size={16} /><b>Deliver</b><small>Enabled once a channel is connected</small></article>
      </section>

      <section className="comms-card">
        <header><div><Target size={16} /><span><b>Communication attention queue</b><small>Open FocusLinc items likely to require outreach or follow-up</small></span></div><em>{queue.length}</em></header>
        <div>{queue.length ? queue.map((item) => (
          <button key={item.id} onClick={() => { setSelected(item); setDraft(""); }}>
            <span><b>{item.title}</b><small>{item.kind?.replace(/_/g," ")} · {item.business_key || item.role || "general"}</small></span><em className={`pill ${item.kind === "waiting_on" ? "cyan" : "amber"}`}>{item.bucket}</em>
          </button>
        )) : <p>No communication-related FocusLinc items are open right now.</p>}</div>
      </section>

      {selected ? (
        <div className="comms-overlay" onMouseDown={() => setSelected(null)}>
          <section onMouseDown={(e) => e.stopPropagation()}>
            <header><div><small>COMMUNICATION DETAIL</small><h2>{selected.title}</h2></div><button onClick={() => setSelected(null)}><X size={17} /></button></header>
            <div className="comms-overlay-body">
              {selected.notes ? <p>{selected.notes}</p> : <p className="hint">No additional notes were captured.</p>}
              <div className="comms-meta"><span><small>Bucket</small><b>{selected.bucket}</b></span><span><small>Type</small><b>{selected.kind?.replace(/_/g," ")}</b></span><span><small>Business</small><b>{selected.business_key || "—"}</b></span></div>
              <button className="btn primary" onClick={draftWithAI} disabled={busy}><Sparkles size={14} /> {busy ? "Drafting…" : "Ask AI to draft"}</button>
              {draft ? <article className="comms-draft"><small>AI DRAFT · REVIEW BEFORE USE</small><p>{draft}</p><div className="comms-delivery-note"><Send size={13} /> Sending is intentionally disabled until an email/SMS provider is connected.</div></article> : null}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
